// ── reports-collection: useCollection client adapter for /reports ───────────

import { useMemo } from "react";
import {
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingArtifactDto, CoachingReportType } from "../../services/coaching-dtos";
import { reportTypeLabels } from "../../domain/report-taxonomy";
import { readDirectoryInitialState } from "../directory-collection-state";
import { buildReportsCollectionColumns as buildReportsColumns, type ReportRow } from "./reports-columns";
import { reportStatusLabel } from "./reports-helpers";

export const REPORTS_PAGE_SIZE = 25;

export function buildReportsCollectionColumns(): CollectionColumnDef<ReportRow>[] {
  return buildReportsColumns() as CollectionColumnDef<ReportRow>[];
}

export interface ReportCohortOption {
  id: string;
  label: string;
}

export function typeOptionsFromReports(rows: CoachingArtifactDto[]): CoachingReportType[] {
  const seen = new Set<CoachingReportType>();
  for (const row of rows) if (row.report_type) seen.add(row.report_type);
  return Array.from(seen);
}

export function statusOptionsFromReports(rows: CoachingArtifactDto[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const status = (row.status ?? "").trim();
    if (status) seen.add(status);
  }
  return Array.from(seen).sort();
}

export function cohortOptionsFromReports(rows: CoachingArtifactDto[]): ReportCohortOption[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const name = (row.cohort_name ?? "").trim();
    if (name) seen.add(name);
  }
  return Array.from(seen, (name) => ({ id: name, label: name })).sort((a, b) => a.label.localeCompare(b.label));
}

export function buildReportsFilterDefs(rows: CoachingArtifactDto[]): CollectionFilterDef<ReportRow>[] {
  const typeOptions = typeOptionsFromReports(rows);
  const statusOptions = statusOptionsFromReports(rows);
  const cohortOptions = cohortOptionsFromReports(rows);
  const filters: CollectionFilterDef<ReportRow>[] = [];

  if (typeOptions.length > 1) {
    filters.push({
      id: "type",
      label: "Audience",
      type: "select",
      placeholder: "All types",
      getValue: (row) => row.report_type ?? "",
      options: typeOptions.map((type) => ({ value: type, label: reportTypeLabels[type] }))
    });
  }

  filters.push({
    id: "status",
    label: "Status",
    type: "select",
    placeholder: "All statuses",
    getValue: (row) => row.status ?? "",
    options: statusOptions.map((status) => ({ value: status, label: reportStatusLabel(status) }))
  });

  if (cohortOptions.length > 1) {
    filters.push({
      id: "cohortId",
      label: "Cohort",
      type: "select",
      placeholder: "All cohorts",
      getValue: (row) => row.cohort_name ?? "",
      options: cohortOptions.map((option) => ({ value: option.id, label: option.label }))
    });
  }

  return filters;
}

export function useReportsCollection(
  rows: CoachingArtifactDto[],
  routePath?: string
): UseCollectionResult<ReportRow> {
  const filters = useMemo(() => buildReportsFilterDefs(rows), [rows]);
  const columns = useMemo(() => buildReportsCollectionColumns(), []);
  const initialState = useMemo(() => readDirectoryInitialState(routePath, REPORTS_PAGE_SIZE), [routePath]);

  return useCollection<ReportRow>({
    mode: "client",
    rows: rows as ReportRow[],
    columns,
    filters,
    pageSize: REPORTS_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-reports",
    initialState,
    getRowKey: (row) => row.id
  });
}
